import React, { useState, useEffect, useMemo, useRef } from 'react';
import { fetchBibleVerse } from './services/bibleApi';
import analyticsService from './services/analytics';
import useServicePlan from './hooks/useServicePlan';
import RunSheetPanel from './components/RunSheetPanel';
import useDisplayOutput from './display/useDisplayOutput';
import { sampleVerses, parseBibleReference } from './lib/bibleReference';
import { autoFormatReferenceInput, buildSearchSuggestions, buildLibrarySuggestions } from './lib/referenceSuggestions';
import useBackgroundImage from './hooks/useBackgroundImage';
import useSyncedStorage from './hooks/useSyncedStorage';
import syncService from './services/syncService';
import AppHeader from './components/AppHeader';
import StagingCard from './components/StagingCard';
import LiveCard from './components/LiveCard';
import useCloudSync from './hooks/useCloudSync';
import useBackgroundSync from './hooks/useBackgroundSync';
import useFlyoutDismiss from './hooks/useFlyoutDismiss';
import useUserActiveCheck from './hooks/useUserActiveCheck';
import useRunSheetKeys from './hooks/useRunSheetKeys';
import AiToolsView from './components/AiToolsView';
import SongsView from './components/SongsView';
import BibleView from './components/BibleView';
import Sidebar from './components/Sidebar';
import HelpModal from './components/HelpModal';
import useDebugLog from './hooks/useDebugLog';
import useMicrophone from './hooks/useMicrophone';
import useVoiceDetection from './hooks/useVoiceDetection';
import useAiSongFinder from './hooks/useAiSongFinder';
import useSongLibrary from './hooks/useSongLibrary';
import useSermonSummary from './hooks/useSermonSummary';
import { logActivity } from './lib/activityLog';
import { toast } from './lib/toast';
import Toaster from './components/Toaster';
import GettingStarted from './components/GettingStarted';
import ProductNotice from './components/ProductNotice';
import AppTour from './components/AppTour';
import { APP_VERSION } from './version';
import { latestRelease } from './changelog';
import { hasUnseenRelease, markReleaseSeen } from './lib/whatsNew';
import useRemoteControl from './remote/useRemoteControl';
import RemotePanel from './remote/RemotePanel';
import useObsOutput from './remote/useObsOutput';
import useSongUsage from './hooks/useSongUsage';
import { contentSlides, DEFAULT_LYRICS_PER_SLIDE, DEFAULT_SCRIPTURE_SLIDE_CHARS } from './lib/slides';
import { DEFAULT_BACKGROUND, DEFAULT_TEXT_EFFECT, backgroundCss, textEffectCss } from './display/themes';

const ChurchApp = ({ user, onLogout }) => {
  // Before any saved data is loaded below: if a different login last used this
  // browser, clear its synced data so it is never uploaded into this account.
  // (Idempotent, so React's double render in development is harmless.)
  const syncPrepared = useRef(false);
  if (!syncPrepared.current) {
    syncService.prepare(user && user.authUserId);
    syncPrepared.current = true;
  }
  // Sidebar flyouts: 'bibleVersion' | 'settings' | 'help' | null
  const [activeMenu, setActiveMenu] = useState(null);
  // Focused workspace: which main panel is shown next to the pinned Staging/Live column
  const [activeView, setActiveView] = useState('bible'); // 'bible' | 'songs' | 'ai'
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sleekHearSidebarOpen');
    if (saved !== null) return saved !== 'false';
    return typeof window === 'undefined' || window.innerWidth >= 1024; // overlays content below lg
  });
  const [displayContent, setDisplayContent] = useState({ type: 'welcome', content: '' });
  const [stagedContent, setStagedContent] = useState({ type: 'welcome', content: '' });
  const [isLive, setIsLive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [recentPassages, setRecentPassages] = useState([]);
  const [testMode, setTestMode] = useState(false);
  const [isLoadingVerse, setIsLoadingVerse] = useState(false);
  const sheet = useServicePlan();
  // Settings and libraries persist across refreshes so a mid-service reload
  // doesn't wipe the operator's prepared content, and are synced per login.
  const [settings, setSettings] = useSyncedStorage('settings', (stored) => {
    const defaults = {
      fontSize: 48,
      fontColor: '#ffffff',
      backgroundColor: '#000000',
      fontFamily: 'Arial',
      background: DEFAULT_BACKGROUND,
      textEffect: DEFAULT_TEXT_EFFECT,
      bibleVersion: 'KJV',
      audioGain: 10,
      lyricsPerSlide: DEFAULT_LYRICS_PER_SLIDE,
      scriptureSlideChars: DEFAULT_SCRIPTURE_SLIDE_CHARS,
      darkMode: true
    };
    const merged = { ...defaults, ...(stored || {}) };
    // Guard against a persisted version that's no longer offered (e.g. a
    // translation we can no longer serve real text for) — fall back to KJV.
    const AVAILABLE = ['KJV','ASV','WEB','BBE','DARBY','YLT','NIV','NIVUK','NASB','AMP','TPT'];
    if (!AVAILABLE.includes(merged.bibleVersion)) merged.bibleVersion = 'KJV';
    return merged;
  });
  // What is on air, split into screen-sized slides (see lib/slides); every screen uses these.
  const liveSlides = useMemo(
    () => contentSlides(displayContent, settings),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [displayContent, settings.lyricsPerSlide, settings.scriptureSlideChars]
  );
  // The staged item as slides too, so the Staging preview shows its first slide, not a whole chapter.
  const stagedSlides = useMemo(
    () => contentSlides(stagedContent, settings),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stagedContent, settings.lyricsPerSlide, settings.scriptureSlideChars]
  );
  const [verseLibrary, setVerseLibrary] = useSyncedStorage('verseLibrary', (stored) => stored || []);
  const [showAddVerseForm, setShowAddVerseForm] = useState(false);
  const [newVerseRef, setNewVerseRef] = useState('');
  const [librarySuggestions, setLibrarySuggestions] = useState([]);
  const [currentLyricLine, setCurrentLyricLine] = useState(0);
  const [helpModal, setHelpModal] = useState(null);
  const [showTour, setShowTour] = useState(false);
  // A new release is announced once; opening the notes or dismissing the notice counts as seen.
  const [unseenRelease, setUnseenRelease] = useState(() => hasUnseenRelease(APP_VERSION));
  const openHelp = (kind) => {
    setHelpModal(kind);
    if (kind === 'changelog' && unseenRelease) { markReleaseSeen(APP_VERSION); setUnseenRelease(false); }
  };
  const dismissNotice = () => { markReleaseSeen(APP_VERSION); setUnseenRelease(false); };

  const isListeningRef = useRef(false); // shared by the microphone meter and speech recognition
  const previousVersionRef = useRef(settings.bibleVersion);
  const sidebarRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('sleekHearSidebarOpen', String(sidebarOpen));
  }, [sidebarOpen]);

  // Cloud sync for this login (see hooks/useCloudSync).
  useCloudSync();

  // Background images follow the login across computers (see services/backgroundSync).
  useBackgroundSync();

  // Close sidebar flyouts on outside click or Escape
  useFlyoutDismiss(activeMenu, setActiveMenu, sidebarRef);

  // Check if user is still active (in case admin deactivated them)
  useUserActiveCheck(user, onLogout);

  const { debugLog, addDebugLog } = useDebugLog();

  const {
    micPermission, audioDevices, selectedDeviceId, setSelectedDeviceId, audioLevel,
    startAudioMonitoring, stopAudioMonitoring, testMicrophone,
  } = useMicrophone({ addDebugLog, isListeningRef, audioGain: settings.audioGain });

  const {
    songLibrary, songSearch, setSongSearch, showAddSongForm, setShowAddSongForm,
    newSongTitle, setNewSongTitle, newSongLyrics, setNewSongLyrics, newSongCcli, setNewSongCcli,
    onlineSearchQuery, setOnlineSearchQuery, onlineSearchResults, isSearchingOnline,
    addToSongLibrary, removeFromSongLibrary, clearSongLibrary, addFoundSongToLibrary,
    searchSongsOnline, clearOnlineSearchResults,
  } = useSongLibrary({ addDebugLog, logActivity });

  const { songUsage, recordSongUse, clearSongUsage } = useSongUsage(songLibrary);

  const {
    groqEnabled, aiSongListening, aiSongTranscript, aiSongResults, aiSongSearching,
    aiSongMode, setAiSongMode, aiSingLyrics, toggleAiSongListening, clearAiSongResults,
  } = useAiSongFinder({ addDebugLog, logActivity });

  // Fetch every scripture in the run-sheet now, while online, so the verses are
  // cached for a connection drop (the service worker keeps the responses).
  const prepareRunSheetOffline = async () => {
    const refs = [...new Set(sheet.items.filter(i => i.kind === 'scripture').map(i => i.reference))];
    const results = await Promise.all(refs.map(async (ref) => {
      try {
        const result = await fetchBibleVerse(ref, settings.bibleVersion);
        return result && result.success ? { ref, ok: true } : { ref, ok: false };
      } catch {
        return { ref, ok: false };
      }
    }));
    return {
      total: refs.length,
      cached: results.filter(r => r.ok).length,
      failed: results.filter(r => !r.ok).map(r => r.ref)
    };
  };

  const getVerseText = async (reference) => {
    try {
      setIsLoadingVerse(true);
      addDebugLog(`📖 Fetching ${reference} from API...`);

      const result = await fetchBibleVerse(reference, settings.bibleVersion);

      if (result.success) {
        addDebugLog(`✅ Loaded ${reference}`);
        if (result.fallback) {
          addDebugLog(`⚠️ Using KJV (${result.requestedVersion} unavailable)`);
        }
        return result.text;
      } else {
        const normalized = reference.toLowerCase();
        if (sampleVerses[normalized]) {
          addDebugLog(`📝 Using cached verse for ${reference}`);
          return sampleVerses[normalized];
        }
        addDebugLog(`❌ Failed to load ${reference}`);
        return result.text || `Unable to load ${reference}`;
      }
    } catch (error) {
      addDebugLog(`❌ Error: ${error.message}`);
      const normalized = reference.toLowerCase();
      if (sampleVerses[normalized]) {
        return sampleVerses[normalized];
      }
      return `Error loading ${reference}`;
    } finally {
      setIsLoadingVerse(false);
    }
  };

  const displayPassageByReference = async (reference) => {
    setIsLoadingVerse(true);
    addDebugLog(`📖 Preparing ${reference} for staging...`);
    analyticsService.trackEvent('bible_search', {
      reference: reference
    });
    logActivity('verse_searched', {
      reference: reference,
      version: settings.bibleVersion
    });
    const content = await getVerseText(reference);

    setStagedContent({
      type: 'scripture',
      reference: reference,
      content: content,
      version: settings.bibleVersion
    });
    setRecentPassages(prev => [reference, ...prev.filter(r => r !== reference)].slice(0, 10));
    addDebugLog(`✅ ${reference} staged - ready to send live`);
  };

  // Live scripture detection from the preacher's voice (see hooks/useVoiceDetection).
  const {
    isListening, isRecognizing, transcribedText, interimText, detectedPassages, toggleListening,
  } = useVoiceDetection({
    addDebugLog, logActivity, displayPassageByReference, isListeningRef,
    startAudioMonitoring, stopAudioMonitoring,
  });

  const {
    sermonSummary, isSummarizing, summarizeSermon, downloadSummary, copySummary, printHandout,
    sermonTitle, setSermonTitle, speaker, setSpeaker,
  } = useSermonSummary({ transcribedText, addDebugLog, logActivity });

  const sendStagedContentLive = () => {
    if (stagedContent && stagedContent.type === 'scripture') {
      setDisplayContent({
        type: 'scripture',
        reference: stagedContent.reference,
        content: stagedContent.content,
        version: stagedContent.version
      });
      setCurrentLyricLine(0);
      addDebugLog(`📺 Sent live: ${stagedContent.reference}`);
      if (!stagedContent.custom) {
        analyticsService.trackEvent('verse_displayed', {
          reference: stagedContent.reference,
          version: stagedContent.version
        });
        logActivity('verse_displayed', {
          reference: stagedContent.reference,
          version: stagedContent.version
        });
      }
    } else if (stagedContent && stagedContent.type === 'song') {
      setDisplayContent({
        type: 'song',
        title: stagedContent.title,
        content: stagedContent.content
      });
      setCurrentLyricLine(0);
      addDebugLog(`📺 Sent live: ${stagedContent.title}`);
      recordSongUse(stagedContent.title); // CCLI usage log
      analyticsService.trackEvent('song_displayed', {
        title: stagedContent.title
      });
      logActivity('song_displayed', {
        title: stagedContent.title
      });
    }
  };

  const clearStagedContent = () => {
    if (stagedContent && stagedContent.type === 'scripture') {
      addDebugLog(`❌ Cleared staged: ${stagedContent.reference}`);
    } else if (stagedContent && stagedContent.type === 'song') {
      addDebugLog(`❌ Cleared staged: ${stagedContent.title}`);
    }
    setStagedContent({ type: 'welcome', content: '' });
  };

  const clearLiveContent = () => {
    if (displayContent && displayContent.type === 'scripture') {
      addDebugLog(`📺 Cleared live: ${displayContent.reference}`);
    } else if (displayContent && displayContent.type === 'song') {
      addDebugLog(`📺 Cleared live: ${displayContent.title}`);
    }
    setDisplayContent({ type: 'welcome', content: '' });
  };

  // --- Run-sheet: stage a plan item through the existing staging pipeline ---
  const stagePlanItem = async (item) => {
    if (item.kind === 'scripture') {
      await displayPassageByReference(item.reference);
    } else if (item.kind === 'song') {
      setStagedContent({ type: 'song', title: item.title, content: item.content });
      setCurrentLyricLine(0);
      addDebugLog(`🎵 ${item.title} staged from run-sheet`);
    } else {
      // Custom text slide: rendered like a scripture card (title + body), no analytics.
      setStagedContent({ type: 'scripture', reference: item.title, content: item.content, custom: true });
      addDebugLog(`📝 ${item.title} staged from run-sheet`);
    }
  };

  const addStagedToRunSheet = () => {
    if (stagedContent.type === 'scripture' && !stagedContent.custom) {
      sheet.addItem({ kind: 'scripture', title: stagedContent.reference, reference: stagedContent.reference, version: stagedContent.version });
      toast.success(`Added ${stagedContent.reference} to the run-sheet`);
    } else if (stagedContent.type === 'song') {
      sheet.addItem({ kind: 'song', title: stagedContent.title, content: stagedContent.content });
      toast.success(`Added "${stagedContent.title}" to the run-sheet`);
    }
  };

  const addToVerseLibrary = () => {
    if (!newVerseRef.trim()) {
      toast.error('Please enter a verse reference');
      return;
    }

    const parsed = parseBibleReference(newVerseRef);
    if (!parsed) {
      toast.error('That is not a verse reference. Try "John 3:16"');
      return;
    }

    const newVerse = {
      reference: parsed.reference
    };

    setVerseLibrary(prev => [...prev, newVerse]);
    setNewVerseRef('');
    setLibrarySuggestions([]);
    setShowAddVerseForm(false);
    addDebugLog(`📚 Added to library: ${newVerse.reference}`);
    toast.success(`Saved ${newVerse.reference} to your verses`);
  };

  const removeFromVerseLibrary = (index) => {
    setVerseLibrary(prev => prev.filter((_, i) => i !== index));
    addDebugLog(`📚 Removed from library`);
  };

  const clearVerseLibrary = () => {
    if (verseLibrary.length > 0 && window.confirm(`Clear all ${verseLibrary.length} verses from library?`)) {
      setVerseLibrary([]);
      addDebugLog(`📚 Cleared verse library`);
    }
  };

  const clearRecentPassages = () => {
    if (recentPassages.length > 0 && window.confirm(`Clear all ${recentPassages.length} recent passages?`)) {
      setRecentPassages([]);
      addDebugLog(`📖 Cleared recent passages`);
    }
  };

  const displaySong = (song) => {
    setStagedContent({
      type: 'song',
      title: song.title,
      content: song.lyrics
    });
    setCurrentLyricLine(0);
    addDebugLog(`🎵 ${song.title} staged - ready to send live`);
    logActivity('song_staged', {
      title: song.title
    });
  };

  const updateSettings = (newSettings) => {
    setSettings(newSettings);
    analyticsService.trackEvent('settings_changed', {
      changedSettings: Object.keys(newSettings)
    });
    logActivity('settings_changed', {
      changedSettings: Object.keys(newSettings)
    });
  };

  // Step through the live slides (song lines/stanzas, or the parts of a long passage).
  const nextLyricLine = () => {
    if (currentLyricLine < liveSlides.length - 1) {
      setCurrentLyricLine(prev => prev + 1);
      addDebugLog(`➡️ Next slide: ${currentLyricLine + 2}/${liveSlides.length}`);
    }
  };

  const prevLyricLine = () => {
    if (currentLyricLine > 0) {
      setCurrentLyricLine(prev => prev - 1);
      addDebugLog(`⬅️ Previous slide: ${currentLyricLine}/${liveSlides.length}`);
    }
  };

  const resetLyricLine = () => {
    setCurrentLyricLine(0);
    addDebugLog(`🔄 Reset to first line`);
  };

  // If the slides get fewer (e.g. a larger lyrics-per-slide setting), stay on a slide that exists.
  useEffect(() => {
    if (liveSlides.length > 0 && currentLyricLine > liveSlides.length - 1) setCurrentLyricLine(liveSlides.length - 1);
  }, [liveSlides.length, currentLyricLine]);

  // Keyboard control of the run-sheet and the live slides (see hooks/useRunSheetKeys).
  useRunSheetKeys({
    sheet, stagePlanItem, sendStagedContentLive, clearLiveContent, nextLyricLine, prevLyricLine,
    onHelp: () => openHelp('shortcuts'),
  });

  // Phone remote: runs the same actions as the keyboard (see remote/useRemoteControl).
  const [showRemote, setShowRemote] = useState(false);
  const remote = useRemoteControl({
    sheet, stagedContent, displayContent, isLive, currentLyricLine, slides: liveSlides,
    actions: { stagePlanItem, sendStagedContentLive, clearLiveContent, nextLyricLine, prevLyricLine },
  });

  // Livestream output for OBS shares the remote's pairing code (see remote/useObsOutput).
  useObsOutput({ code: remote.code, isLive, content: displayContent, currentLine: currentLyricLine, slides: liveSlides, style: settings });

  const handleSearch = async (query) => {
    if (!query.trim()) return;

    const parsed = parseBibleReference(query);
    if (parsed) {
      displayPassageByReference(parsed.reference);
      setSearchQuery('');
      setSearchSuggestions([]);
    } else {
      toast.error('Couldn\'t find that reference. Try a book and chapter like "John 3", a verse like "John 3:16", or a range like "John 3:16-18".');
    }
  };

  const handleSearchInputChange = (value) => {
    setSearchQuery(autoFormatReferenceInput(value));
  };

  const handleLibraryInputChange = (value) => {
    setNewVerseRef(autoFormatReferenceInput(value));
  };

  // Search suggestions effect
  useEffect(() => {
    setSearchSuggestions(buildSearchSuggestions(searchQuery));
  }, [searchQuery]);

  // Library input suggestions effect
  useEffect(() => {
    setLibrarySuggestions(buildLibrarySuggestions(newVerseRef));
  }, [newVerseRef]);

  // Projector output: the /display page is fed over a BroadcastChannel.
  const bgImageId = settings.background && settings.background.type === 'image' ? settings.background.imageId : null;
  const { url: bgImageUrl, blob: bgImageBlob } = useBackgroundImage(bgImageId);
  const previewBg = backgroundCss(settings.background, settings.backgroundColor, bgImageUrl);
  const previewText = textEffectCss(settings.textEffect);
  const nextPlanItem = sheet.items[sheet.currentIndex + 1];
  const { open: openDisplay, connected: displayConnected, openStage, stageConnected } = useDisplayOutput({
    isLive,
    content: displayContent,
    currentLine: currentLyricLine,
    slides: liveSlides,
    style: {
      backgroundColor: settings.backgroundColor,
      fontColor: settings.fontColor,
      fontSize: settings.fontSize,
      fontFamily: settings.fontFamily,
      background: settings.background,
      textEffect: settings.textEffect
    },
    backgroundImage: bgImageBlob,
    stage: {
      planName: sheet.plan.name,
      index: sheet.currentIndex,
      total: sheet.items.length,
      nextLabel: nextPlanItem ? (nextPlanItem.kind === 'scripture' ? nextPlanItem.reference : nextPlanItem.title) : ''
    }
  });

  const openStageWindow = async () => {
    const opened = await openStage();
    if (!opened) addDebugLog('⚠️ Stage window was blocked — allow pop-ups for this site');
  };

  const openDisplayWindow = async () => {
    const opened = await openDisplay();
    if (opened) {
      analyticsService.trackEvent('display_window_opened', {
        timestamp: new Date().toISOString()
      });
      logActivity('display_window_opened', {});
    } else {
      addDebugLog('⚠️ Display window was blocked — allow pop-ups for this site');
    }
  };

  // Auto-refresh verse when Bible version changes
  useEffect(() => {
    const refreshCurrentVerse = async () => {
      if (previousVersionRef.current !== settings.bibleVersion) {
        // Refresh Live display content
        if (displayContent.type === 'scripture' && displayContent.reference) {
          addDebugLog(`🔄 Refreshing Live ${displayContent.reference} with ${settings.bibleVersion}...`);
          setIsLoadingVerse(true);

          const result = await fetchBibleVerse(displayContent.reference, settings.bibleVersion);

          let newContent = '';
          if (result.success) {
            newContent = result.text;
            if (result.fallback === true) {
              addDebugLog(`⚠️ Using KJV fallback (${result.requestedVersion} unavailable)`);
            } else {
              addDebugLog(`✅ Loaded ${displayContent.reference} in ${settings.bibleVersion}`);
            }
          } else {
            newContent = result.text || `Unable to load ${displayContent.reference}`;
            addDebugLog(`❌ Failed to load in ${settings.bibleVersion}`);
          }

          setIsLoadingVerse(false);
          setDisplayContent({
            type: 'scripture',
            reference: displayContent.reference,
            content: newContent,
            version: settings.bibleVersion
          });
          addDebugLog(`✅ Live display refreshed to ${settings.bibleVersion}`);
        }

        // Refresh Staged content
        if (stagedContent.type === 'scripture' && stagedContent.reference) {
          addDebugLog(`🔄 Refreshing Staging ${stagedContent.reference} with ${settings.bibleVersion}...`);

          const result = await fetchBibleVerse(stagedContent.reference, settings.bibleVersion);

          let newContent = '';
          if (result.success) {
            newContent = result.text;
            if (result.fallback === true) {
              addDebugLog(`⚠️ Staging: Using KJV fallback`);
            } else {
              addDebugLog(`✅ Staging loaded ${stagedContent.reference} in ${settings.bibleVersion}`);
            }
          } else {
            newContent = result.text || `Unable to load ${stagedContent.reference}`;
            addDebugLog(`❌ Staging: Failed to load in ${settings.bibleVersion}`);
          }

          setStagedContent({
            type: 'scripture',
            reference: stagedContent.reference,
            content: newContent,
            version: settings.bibleVersion
          });
          addDebugLog(`✅ Staging refreshed to ${settings.bibleVersion}`);
        }

        previousVersionRef.current = settings.bibleVersion;
      }
    };

    refreshCurrentVerse();
  }, [settings.bibleVersion, displayContent.type, displayContent.reference, stagedContent.type, stagedContent.reference, addDebugLog]);

  // One plain sentence about what the congregation can see, shown beside the LIVE switch.
  const liveLabel = displayContent.type === 'scripture' ? displayContent.reference
    : displayContent.type === 'song' ? displayContent.title : '';
  const liveStatus = isLive
    ? (liveLabel ? `On screen: ${liveLabel}` : 'On screen: welcome')
    : (liveLabel ? `Screen is blank · ready: ${liveLabel}` : 'Screen is blank');

  // First-run checklist (see components/GettingStarted); each step ticks itself off.
  const gettingStartedSteps = [
    { label: 'Open the projector display', hint: 'Click Open Display, then drag that window to the projector screen.', done: displayConnected },
    { label: 'Plan or stage something', hint: 'Search a verse below, add a song in Songs, or build the run-sheet.', done: sheet.items.length > 0 || stagedContent.type !== 'welcome' || displayContent.type !== 'welcome' },
    { label: 'Send it live', hint: 'Press Live on the staged item, then switch the top button from OFF AIR to LIVE.', done: isLive && displayContent.type !== 'welcome' },
  ];

  const dm = settings.darkMode;
  const t = {
    // Backgrounds
    pageBg:      dm ? 'bg-slate-900'                     : 'bg-gray-50',
    headerBg:    dm ? 'bg-slate-800 border-slate-700'    : 'bg-white border-gray-200',
    sidebarBg:   dm ? 'bg-slate-800 border-slate-700'    : 'bg-white border-gray-200',
    cardBg:      dm ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-gray-200',
    innerCardBg: dm ? 'bg-slate-900/60 border-slate-700' : 'bg-gray-50 border-gray-200',
    inputBg:     dm ? 'bg-slate-900 border-slate-700 text-slate-50 placeholder-slate-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400',
    dropdownBg:  dm ? 'bg-slate-800 border-slate-700'    : 'bg-white border-gray-200',
    dropdownItem:dm ? 'text-slate-50 hover:bg-slate-700' : 'text-gray-800 hover:bg-gray-100',
    dropdownDiv: dm ? 'border-slate-700'                 : 'border-gray-200',
    // Text
    textPrimary: dm ? 'text-slate-50'   : 'text-gray-900',
    textSecondary:dm? 'text-slate-400'  : 'text-gray-500',
    textMuted:   dm ? 'text-slate-500'  : 'text-gray-400',
    // Row items
    rowBg:       dm ? 'bg-slate-800/80 border-slate-700' : 'bg-white border-gray-200',
    rowHover:    dm ? 'hover:bg-slate-700/50'            : 'hover:bg-gray-50',
    // Scrollbars
    scrollbar:   dm ? 'scrollbar-thumb-slate-700'        : 'scrollbar-thumb-gray-300',
    // Misc
    divider:     dm ? 'border-slate-700'                 : 'border-gray-200',
    selectBg:    dm ? 'bg-slate-900 text-slate-50 border-slate-700' : 'bg-white text-gray-900 border-gray-300',
  };

  return (
    <div className={`h-dvh flex flex-col overflow-hidden relative ${t.pageBg}`}>
      {/* Animated background overlay */}
      <div className={`absolute inset-0 ${
        dm
          ? 'bg-slate-900'
          : 'bg-gradient-to-tr from-blue-50/40 via-white to-blue-50/40'
      }`}></div>

      <AppHeader
        displayConnected={displayConnected}
        dm={dm}
        isLive={isLive}
        liveStatus={liveStatus}
        onLogout={onLogout}
        openDisplayWindow={openDisplayWindow}
        openStageWindow={openStageWindow}
        onOpenRemote={() => setShowRemote(true)}
        remoteConnected={remote.phoneConnected}
        setIsLive={setIsLive}
        setSidebarOpen={setSidebarOpen}
        sidebarOpen={sidebarOpen}
        stageConnected={stageConnected}
        t={t}
      />
      {unseenRelease && <ProductNotice release={latestRelease()} dm={dm} onView={() => openHelp('changelog')} onDismiss={dismissNotice} />}
      {/* Workspace: sidebar + main content */}
      <div className="relative flex flex-1 min-h-0 overflow-hidden">
        {/* Mobile backdrop when the sidebar is expanded over content */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/40 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <Sidebar
          activeMenu={activeMenu}
          activeView={activeView}
          dm={dm}
          isListening={isListening}
          setActiveMenu={setActiveMenu}
          setActiveView={setActiveView}
          setHelpModal={openHelp}
          hasUnseenRelease={unseenRelease}
          onStartTour={() => setShowTour(true)}
          setSettings={setSettings}
          settings={settings}
          sidebarOpen={sidebarOpen}
          sidebarRef={sidebarRef}
          t={t}
          updateSettings={updateSettings}
        />
        {/* Main content: active view + pinned Staging/Live */}
        <div className="flex-1 min-w-0 overflow-y-auto p-3 sm:p-6">
        <div className="grid grid-cols-[minmax(0,1fr)] lg:grid-cols-3 gap-4 sm:gap-6 lg:items-start">

          <BibleView
            activeView={activeView}
            addDebugLog={addDebugLog}
            addToVerseLibrary={addToVerseLibrary}
            audioDevices={audioDevices}
            audioLevel={audioLevel}
            clearRecentPassages={clearRecentPassages}
            clearVerseLibrary={clearVerseLibrary}
            debugLog={debugLog}
            detectedPassages={detectedPassages}
            displayPassageByReference={displayPassageByReference}
            dm={dm}
            handleLibraryInputChange={handleLibraryInputChange}
            handleSearch={handleSearch}
            handleSearchInputChange={handleSearchInputChange}
            intro={<GettingStarted steps={gettingStartedSteps} dm={dm} t={t} />}
            interimText={interimText}
            isListening={isListening}
            isRecognizing={isRecognizing}
            librarySuggestions={librarySuggestions}
            micPermission={micPermission}
            newVerseRef={newVerseRef}
            recentPassages={recentPassages}
            removeFromVerseLibrary={removeFromVerseLibrary}
            searchQuery={searchQuery}
            searchSuggestions={searchSuggestions}
            selectedDeviceId={selectedDeviceId}
            setLibrarySuggestions={setLibrarySuggestions}
            setNewVerseRef={setNewVerseRef}
            setRecentPassages={setRecentPassages}
            setSearchQuery={setSearchQuery}
            setSelectedDeviceId={setSelectedDeviceId}
            setShowAddVerseForm={setShowAddVerseForm}
            setTestMode={setTestMode}
            settings={settings}
            showAddVerseForm={showAddVerseForm}
            t={t}
            testMicrophone={testMicrophone}
            testMode={testMode}
            toggleListening={toggleListening}
            transcribedText={transcribedText}
            updateSettings={updateSettings}
            user={user}
            verseLibrary={verseLibrary}
          />
          <SongsView
            activeView={activeView}
            addAiSongToLibrary={addFoundSongToLibrary}
            addDebugLog={addDebugLog}
            addOnlineSongToLibrary={addFoundSongToLibrary}
            addToSongLibrary={addToSongLibrary}
            aiSingLyrics={aiSingLyrics}
            aiSongListening={aiSongListening}
            aiSongMode={aiSongMode}
            aiSongResults={aiSongResults}
            aiSongSearching={aiSongSearching}
            aiSongTranscript={aiSongTranscript}
            clearAiSongResults={clearAiSongResults}
            clearOnlineSearchResults={clearOnlineSearchResults}
            clearSongLibrary={clearSongLibrary}
            clearSongUsage={clearSongUsage}
            displaySong={displaySong}
            dm={dm}
            groqEnabled={groqEnabled}
            isSearchingOnline={isSearchingOnline}
            logActivity={logActivity}
            newSongCcli={newSongCcli}
            newSongLyrics={newSongLyrics}
            newSongTitle={newSongTitle}
            onlineSearchQuery={onlineSearchQuery}
            onlineSearchResults={onlineSearchResults}
            removeFromSongLibrary={removeFromSongLibrary}
            searchSongsOnline={searchSongsOnline}
            setAiSongMode={setAiSongMode}
            setCurrentLyricLine={setCurrentLyricLine}
            setDisplayContent={setDisplayContent}
            setNewSongCcli={setNewSongCcli}
            setNewSongLyrics={setNewSongLyrics}
            setNewSongTitle={setNewSongTitle}
            setOnlineSearchQuery={setOnlineSearchQuery}
            setShowAddSongForm={setShowAddSongForm}
            setSongSearch={setSongSearch}
            showAddSongForm={showAddSongForm}
            songLibrary={songLibrary}
            songSearch={songSearch}
            songUsage={songUsage}
            t={t}
            toggleAiSongListening={toggleAiSongListening}
          />
          <AiToolsView
            activeView={activeView}
            copySummary={copySummary}
            displayPassageByReference={displayPassageByReference}
            dm={dm}
            downloadSummary={downloadSummary}
            groqEnabled={groqEnabled}
            isSummarizing={isSummarizing}
            printHandout={printHandout}
            sermonSummary={sermonSummary}
            sermonTitle={sermonTitle}
            setSermonTitle={setSermonTitle}
            setSpeaker={setSpeaker}
            speaker={speaker}
            summarizeSermon={summarizeSermon}
            t={t}
            transcribedText={transcribedText}
          />
          {/* PINNED: Staging + Live stay on screen; the run-sheet scrolls below them */}
          <div className="order-first lg:order-none lg:col-span-1 space-y-4 lg:sticky lg:top-0 lg:max-h-[calc(100dvh-8.5rem)] lg:overflow-y-auto no-scrollbar">
            <StagingCard
              dm={dm}
              stagedContent={stagedContent}
              slides={stagedSlides}
              settings={settings}
              previewBg={previewBg}
              previewText={previewText}
              isLoadingVerse={isLoadingVerse}
              addStagedToRunSheet={addStagedToRunSheet}
              sendStagedContentLive={sendStagedContentLive}
              clearStagedContent={clearStagedContent}
            />

            <LiveCard
              dm={dm}
              isLive={isLive}
              displayContent={displayContent}
              currentLyricLine={currentLyricLine}
              slides={liveSlides}
              settings={settings}
              previewBg={previewBg}
              previewText={previewText}
              prevLyricLine={prevLyricLine}
              resetLyricLine={resetLyricLine}
              nextLyricLine={nextLyricLine}
              clearLiveContent={clearLiveContent}
            />

            <RunSheetPanel
              sheet={sheet}
              onStage={stagePlanItem}
              onSendLive={sendStagedContentLive}
              onPrepareOffline={prepareRunSheetOffline}
              dm={dm}
              t={t}
            />
          </div>
        </div>
        </div>
      </div>

      {showRemote && <RemotePanel remote={remote} onClose={() => setShowRemote(false)} dm={dm} t={t} />}
      <Toaster />
      {showTour && <AppTour onClose={() => setShowTour(false)} />}
      <HelpModal kind={helpModal} onClose={() => setHelpModal(null)} dm={dm} t={t} />
    </div>
  );
};

export default ChurchApp;
